/**
 * Left panel of the org structure browser: indented tree with guide lines,
 * member counts, manager avatars, a missing-manager warning and the members a search matched.
 */
import { Avatar, Icon, cn } from '@insio/ui-toolkit';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { useToday } from '@/hooks/use-today';
import { LifecycleStatus, lifecycleState } from '@/utils/lifecycle-status';
import type { OrgTreeNode, OrgTreeUnit } from './use-org-structure';

interface OrgUnitTreeProps {
  nodes: OrgTreeNode<OrgTreeUnit>[];
  selectedId: string | null;
  expandedIds: Set<string>;
  /** While searching, expansion follows the matches — toggles are suppressed. */
  searchActive: boolean;
  onSelect: (id: string) => void;
  onToggle: (id: string) => void;
}

/** A row carries its own node, never the whole tree — `nodes` would be dead weight per level. */
interface OrgUnitTreeRowProps extends Omit<OrgUnitTreeProps, 'nodes'> {
  node: OrgTreeNode<OrgTreeUnit>;
  depth: number;
}

function OrgUnitTreeRow({ node, depth, ...rest }: OrgUnitTreeRowProps) {
  const t = useFeatureTranslation('codeLists.orgStructure');
  const today = useToday();
  const { selectedId, expandedIds, searchActive, onSelect, onToggle } = rest;
  const { unit, children } = node;
  const hasChildren = children.length > 0;
  const isExpanded = expandedIds.has(unit.id);
  const isSelected = selectedId === unit.id;
  const missingManager = !unit.manager;
  const canToggle = hasChildren && !searchActive;
  // A deactivated or out-of-validity unit stays in the tree, dimmed; its detail names why.
  const isDimmed = lifecycleState(unit, today).status !== LifecycleStatus.ACTIVE;

  return (
    <li>
      <div
        role='treeitem'
        aria-selected={isSelected}
        aria-level={depth + 1}
        aria-expanded={hasChildren ? isExpanded : undefined}
        data-testid={`org-unit-row-${unit.code}`}
        className={`group relative flex w-full cursor-pointer items-center gap-sm rounded-lg py-sm pr-sm pl-xs transition-colors ${
          isSelected
            ? 'bg-primary-base text-foreground-white'
            : 'hover:bg-background-muted focus-visible:bg-background-muted'
        }`}
        onClick={() => onSelect(unit.id)}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onSelect(unit.id);
          }
        }}
        tabIndex={0}
      >
        {canToggle ? (
          <button
            type='button'
            aria-label={isExpanded ? t('tree.collapse') : t('tree.expand')}
            className={`flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center rounded transition-colors ${
              isSelected
                ? 'text-foreground-white/70 hover:text-foreground-white'
                : 'text-foreground-disabled hover:text-foreground-base'
            }`}
            onClick={event => {
              event.stopPropagation();
              onToggle(unit.id);
            }}
          >
            <Icon name='chevron-down' size='xs' rotate={isExpanded ? 0 : 270} />
          </button>
        ) : hasChildren ? (
          // Search drives expansion itself, so the chevron is shown as a static marker.
          <span className='flex h-5 w-5 shrink-0 items-center justify-center'>
            <Icon
              name='chevron-down'
              size='xs'
              className={isSelected ? 'text-foreground-white/70' : 'text-foreground-disabled'}
            />
          </span>
        ) : (
          // Leaf rows line up with their siblings' chevrons via a hierarchy dot.
          <span className='flex h-5 w-5 shrink-0 items-center justify-center'>
            <span
              className={`h-1.5 w-1.5 rounded-full ${
                isSelected ? 'bg-foreground-white/50' : 'bg-foreground-disabled'
              }`}
            />
          </span>
        )}

        <span
          className={cn('flex min-w-0 flex-1 flex-col', isDimmed && !isSelected && 'opacity-60')}
        >
          <span className='truncate text-sm font-medium'>{unit.name}</span>
          {/* Found by a member, not by its own code or name — say who. */}
          {node.matchedMembers && (
            <span
              data-testid={`org-unit-matched-members-${unit.code}`}
              className={cn(
                'flex min-w-0 items-center gap-xs text-xs',
                isSelected ? 'text-foreground-white/70' : 'text-foreground-muted'
              )}
            >
              <Icon name='user' size='xs' className='shrink-0' />
              <span className='truncate'>{node.matchedMembers.join(', ')}</span>
            </span>
          )}
        </span>

        {missingManager && (
          <span
            title={t('tree.noManager')}
            data-testid={`org-unit-warning-${unit.code}`}
            className='flex shrink-0 items-center'
          >
            <Icon
              name='triangle-exclamation'
              size='xs'
              className={isSelected ? 'text-secondary-base' : 'text-warn-border'}
            />
          </span>
        )}

        {/* Member count as a quiet pill, so the number reads as data not as a label */}
        <span
          className={`shrink-0 rounded-full px-xs py-2xs text-xs tabular-nums transition-colors ${
            isSelected
              ? 'bg-foreground-white/15 text-foreground-white'
              : 'bg-background-muted text-foreground-muted group-hover:bg-background-disabled'
          }`}
        >
          {unit.memberCount}
        </span>

        {unit.manager ? (
          <Avatar size='xs' name={unit.manager.displayName} />
        ) : (
          <span className='w-6 shrink-0' />
        )}
      </div>

      {hasChildren && isExpanded && (
        <ul
          role='group'
          className='ml-md border-l border-border-base pl-sm'
          data-testid={`org-unit-children-${unit.code}`}
        >
          {children.map(child => (
            <OrgUnitTreeRow key={child.unit.id} node={child} depth={depth + 1} {...rest} />
          ))}
        </ul>
      )}
    </li>
  );
}

export function OrgUnitTree({ nodes, ...rest }: OrgUnitTreeProps) {
  return (
    <ul role='tree' data-testid='org-unit-tree' className='flex flex-col gap-2xs'>
      {nodes.map(node => (
        <OrgUnitTreeRow key={node.unit.id} node={node} depth={0} {...rest} />
      ))}
    </ul>
  );
}
